import {
  ContentItem,
  ContentItems,
  ContentWrapper,
  Flex,
  Text,
  TitledText,
} from '@/components';
import { PinpointMarker } from '@/features/comment/components/pinpoint';
import { SegmentSheetModel } from '@/features/project/projects/_models/project';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';
import { ProjectInformationHeader } from './ProjectInformationHeader';
import {
  ProjectCategoryPinpointItem,
  ProjectFiscalYearPinpointItem,
  ProjectGroupPinpointItem,
  ProjectLegalBasesPinpointItem,
  ProjectMainCostPinpointItem,
  ProjectNumberPinpointItem,
  ProjectOldProjectNumberPinpointItem,
  ProjectOrderNumberPinpointItem,
  ProjectOverviewPinpointItem,
  ProjectPeriodPinpointItem,
  ProjectPlanPinpointItem,
  ProjectPurposePinpointItem,
  ProjectRelatedProjectsPinpointItem,
  ProjectRemarkPinpointItem,
  ProjectSheetTypePinpointItem,
  ProjectTaskPinpointItem,
} from './pinpoint-items/basic-information-pinpoint-fields';

type Props = z.infer<typeof SegmentSheetModel> & {
  historySavedAt: string | undefined;
};

export const SsProjectInformation: FC<Props> = (props) => {
  const {
    id,
    fiscalYear,
    relatedProjects,
    projectNumber,
    oldProjectNumber,
    startYear,
    endYear,
    isUnknownStart,
    isNoScheduledEnd,
    category,
    purpose,
    overview,
    overviewUrl,
    legalBases,
    plans,
    task,
    mainCosts,
    remark,
    sortOrder,
    name,
    sheetType,
    ...rest
  } = SegmentSheetModel.parse(props);
  const { t } = useTranslation();

  const segmentUnitConcept = rest.segmentUnitConcept;

  return (
    <>
      <ProjectInformationHeader
        ministryId={rest.ministryId}
        historySavedAt={props.historySavedAt}
        organization={rest.organization}
        relatedOrganizations={rest.relatedOrganizations}
        otherResponsibleOrganizations={rest.otherResponsibleOrganizations}
        peopleInCharge={rest.peopleInCharge}
        businessManagers={rest.businessManagers}
        snapshotId={rest.snapshotId}
        pinpointSheetType="SS"
      />

      <ContentWrapper title={t('basic-information')}>
        <ContentItem borderBottom={0}>
          <ContentItems
            items={[
              <ProjectNumberPinpointItem
                key="project-number"
                projectNumber={projectNumber}
                pinpointSheetType={sheetType}
                projectId={id}
              />,
              <ProjectGroupPinpointItem
                key="project-group"
                projectId={id}
                pinpointSheetType={sheetType}
              />,
            ]}
          />
        </ContentItem>
        <Flex vertical gap={4}>
          <ProjectSheetTypePinpointItem
            pinpointSheetType={sheetType}
            projectId={id}
          />
          <Text variant="h4" text={name} />
        </Flex>
        <ContentItem>
          <ContentItems
            items={[
              <PinpointMarker
                key="independent-administrative-agency"
                pinpoint={{
                  path: { project: id },
                  field: 'ss.independent-administrative-agency',
                }}
              >
                <TitledText
                  key="independent-administrative-agency"
                  title={t('independent-administrative-agency')}
                  text={rest.independentAdministrativeAgency?.name}
                />
              </PinpointMarker>,
            ]}
          />
        </ContentItem>
        <ContentItem>
          <ContentItems
            mode="fillRight"
            items={[
              <ProjectFiscalYearPinpointItem
                key="fiscal-year"
                projectId={id}
                fiscalYear={fiscalYear}
                pinpointSheetType={sheetType}
              />,
              <ProjectCategoryPinpointItem
                key="project-category"
                projectId={id}
                category={category}
                pinpointSheetType={sheetType}
              />,
              <ProjectPeriodPinpointItem
                key="period"
                projectId={id}
                startYear={startYear}
                isUnknownStart={isUnknownStart}
                endYear={endYear}
                isNoScheduledEnd={isNoScheduledEnd}
                pinpointSheetType={sheetType}
              />,
            ]}
          />
        </ContentItem>

        <ContentItem borderBottom={0} paddingBottom={0}>
          <ContentItems
            items={[
              <ProjectRelatedProjectsPinpointItem
                key="related-projects"
                projectId={id}
                relatedProjects={relatedProjects}
                pinpointSheetType={sheetType}
              />,
              <ProjectMainCostPinpointItem
                key="main-cost"
                projectId={id}
                cost={mainCosts}
                pinpointSheetType={sheetType}
              />,
            ]}
          />
        </ContentItem>

        <ContentItem paddingBottom={0} borderBottom={0}>
          <ContentItems
            items={[
              <PinpointMarker
                key="segment-unit-concept"
                pinpoint={{
                  path: { project: id },
                  field: 'ss.segment-unit-concept',
                }}
              >
                <TitledText
                  key="segment-unit-concept"
                  title={t('segment-unit-concept')}
                  text={segmentUnitConcept}
                />
              </PinpointMarker>,
            ]}
          />
        </ContentItem>
      </ContentWrapper>

      <ContentWrapper title={t('overview-and-purpose')}>
        <ContentItem>
          <ContentItems
            items={[
              <ProjectPurposePinpointItem
                key="project-purpose"
                projectId={id}
                purpose={purpose}
                pinpointSheetType={sheetType}
              />,
              <ProjectTaskPinpointItem
                key="project-task"
                projectId={id}
                task={task}
                pinpointSheetType={sheetType}
              />,
            ]}
          />
        </ContentItem>

        <ContentItem paddingBottom={0} borderBottom={0}>
          <ContentItems
            items={[
              <ProjectOverviewPinpointItem
                key="overview"
                projectId={id}
                pinpointSheetType={sheetType}
                overview={overview}
                overviewUrl={overviewUrl}
              />,
            ]}
          />
        </ContentItem>
      </ContentWrapper>

      <ContentWrapper title={t('legal-bases')}>
        <ContentItem borderBottom={0} paddingBottom={0}>
          <Flex vertical gap={24}>
            <ProjectLegalBasesPinpointItem
              projectId={id}
              pinpointSheetType={sheetType}
              legalBases={legalBases ?? []}
            />
            <ProjectPlanPinpointItem
              projectId={id}
              pinpointSheetType={sheetType}
              plans={plans ?? []}
            />
          </Flex>
        </ContentItem>
      </ContentWrapper>

      <ContentWrapper borderBottom={0} title={t('others')}>
        <ContentItem>
          <ContentItems
            items={[
              <ProjectOldProjectNumberPinpointItem
                key="old-project-number"
                projectId={id}
                oldProjectNumber={oldProjectNumber}
                pinpointSheetType={sheetType}
              />,
              <ProjectOrderNumberPinpointItem
                key="project-order-number"
                projectId={id}
                sortOrder={sortOrder}
                pinpointSheetType={sheetType}
              />,
              <ProjectRemarkPinpointItem
                key="remark"
                projectId={id}
                remark={remark}
                pinpointSheetType={sheetType}
              />,
            ]}
          />
        </ContentItem>
      </ContentWrapper>
    </>
  );
};
